/* Douga Douga – scène immersive v2 : bouteilles en relief (WebGL) + sculptures de particules 3D */
(() => {
  'use strict';
  const RM = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FINE = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const D2R = Math.PI / 180;
  /* mobile : moins de particules, résolution réduite ; appareils modestes ou « économie de données » : image fixe */
  const MOB = matchMedia('(max-width: 989px), (pointer: coarse)').matches;
  const NAVC = navigator.connection || {};
  const LITE = !!NAVC.saveData || /(^|-)2g$/.test(NAVC.effectiveType || '') || (navigator.deviceMemory && navigator.deviceMemory <= 2) || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2);
  const DPR = () => Math.min(MOB ? 1.5 : 2, window.devicePixelRatio || 1);

  /* =========================================================
     1. Formes en volume pour les particules (x, y vers le bas, z vers l'écran)
     ========================================================= */
  function bez(p0, p1, p2, p3, t) {
    const u = 1 - t;
    return [u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0], u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1]];
  }
  function ell(cx, cy, cz, rx, ry, rot, n = 48) {
    const out = [], c = Math.cos(rot), s = Math.sin(rot);
    for (let i = 0; i <= n; i++) { const a = (i / n) * Math.PI * 2, x = Math.cos(a) * rx, y = Math.sin(a) * ry; out.push([cx + x * c - y * s, cy + x * s + y * c, cz]); }
    return out;
  }
  function hring(cx, cy, cz, r, n = 60) {
    const out = [];
    for (let i = 0; i <= n; i++) { const a = (i / n) * Math.PI * 2; out.push([cx + Math.cos(a) * r, cy, cz + Math.sin(a) * r]); }
    return out;
  }
  const SHAPES = {
    branch() {
      const P0 = [-0.95, 0.6], P1 = [-0.35, 0.5], P2 = [0.2, -0.1], P3 = [0.92, -0.62];
      const zs = (t) => 0.2 * Math.sin(t * Math.PI) - 0.05;
      const stem = []; for (let i = 0; i <= 120; i++) { const p = bez(P0, P1, P2, P3, i / 120); stem.push([p[0], p[1], zs(i / 120)]); }
      const lines = [stem];
      [0.12, 0.2, 0.3, 0.38, 0.48, 0.56, 0.66, 0.74, 0.84, 0.93].forEach((t, k) => {
        const a = bez(P0, P1, P2, P3, t), b = bez(P0, P1, P2, P3, Math.min(1, t + 0.01)), z0 = zs(t), side = k % 2 ? 1 : -1;
        const ang = Math.atan2(b[1] - a[1], b[0] - a[0]) + side * 0.95;
        const L = 0.36 * (1 - t * 0.45), w = 0.075 * (1 - t * 0.3);
        const dx = Math.cos(ang), dy = Math.sin(ang), nx = -dy, ny = dx;
        const up = [], lo = [];
        for (let i = 0; i <= 24; i++) {
          const u = i / 24, cx = a[0] + dx * L * u, cy = a[1] + dy * L * u, o = w * Math.sin(Math.PI * Math.pow(u, 0.8)), z = z0 + side * 0.34 * u;
          up.push([cx + nx * o, cy + ny * o, z + o * 0.6]); lo.push([cx - nx * o, cy - ny * o, z - o * 0.6]);
        }
        lines.push(up, lo, [[a[0], a[1], z0], [a[0] + dx * L * 0.92, a[1] + dy * L * 0.92, z0 + side * 0.31]]);
      });
      [0.34, 0.6, 0.8].forEach((t, k) => {
        const a = bez(P0, P1, P2, P3, t), b = bez(P0, P1, P2, P3, t + 0.01), side = k % 2 ? 1 : -1;
        const ang = Math.atan2(b[1] - a[1], b[0] - a[0]) + Math.PI / 2 * side;
        const ox = a[0] + Math.cos(ang) * 0.13, oy = a[1] + Math.sin(ang) * 0.13, oz = zs(t) - side * 0.22;
        lines.push(ell(ox, oy, oz, 0.05, 0.075, ang, 30), hring(ox, oy, oz, 0.055, 26));
      });
      return lines;
    },
    mountains() {
      const r1 = [[-1.15, 0.42], [-0.86, 0.1], [-0.7, 0.24], [-0.42, -0.36], [-0.24, -0.08], [-0.02, -0.56], [0.2, -0.14], [0.38, -0.34], [0.6, 0.02], [0.82, -0.16], [1.15, 0.36]];
      const back = r1.map(([x, y]) => [x, y, -0.34]);
      const front = r1.map(([x, y], i) => [x * 0.96 + 0.06, y * 0.55 + 0.32 + (i % 2 ? 0.04 : 0), 0.22]);
      const far = r1.map(([x, y], i) => [x * 0.8 - 0.12, y * 0.7 - 0.12 + (i % 3 ? 0.03 : 0), -0.7]);
      const snow = [[-0.5, -0.24, -0.34], [-0.42, -0.36, -0.34], [-0.34, -0.25, -0.34]], snow2 = [[-0.1, -0.42, -0.34], [-0.02, -0.56, -0.34], [0.07, -0.43, -0.34]];
      const ridges = [];
      [3, 5, 7].forEach((i) => ridges.push([back[i], [front[i][0], front[i][1], 0.22]]));
      return [back, front, far, snow, snow2, ...ridges, [[-1.1, 0.62, 0.5], [1.1, 0.62, 0.5]], [[-0.8, 0.74, 0.62], [0.9, 0.74, 0.62]], ell(0.62, -0.72, -0.85, 0.12, 0.12, 0, 40)];
    },
    drop() {
      const prof = (t) => [Math.abs(0.62 * Math.sin(t) * Math.pow(Math.abs(Math.sin(t / 2)), 1.25)), -0.92 * Math.cos(t) - 0.06];
      const lines = [];
      for (let m = 0; m < 10; m++) {
        const ph = (m / 10) * Math.PI * 2 + 0.15, c = Math.cos(ph), s = Math.sin(ph), l = [];
        for (let i = 0; i <= 60; i++) { const [r, y] = prof((i / 60) * Math.PI); l.push([r * c, y, r * s]); }
        lines.push(l);
      }
      [0.3, 0.45, 0.6, 0.72, 0.84].forEach((f) => { const [r, y] = prof(f * Math.PI); lines.push(hring(0, y, 0, r, 50)); });
      lines.push(hring(0, 0.98, 0, 0.95, 80), hring(0, 0.98, 0, 0.6, 60));
      return lines;
    },
    motif() {
      const d = (s, cx, cy, cz) => [[cx, cy - 0.85 * s, cz], [cx + 0.7 * s, cy, cz], [cx, cy + 0.85 * s, cz], [cx - 0.7 * s, cy, cz], [cx, cy - 0.85 * s, cz]];
      const zig = (y, a, z) => { const o = []; for (let i = 0; i <= 16; i++) o.push([-1.15 + i * 0.14375, y + (i % 2 ? -a : a), z]); return o; };
      const D1 = d(1, 0, 0, -0.2), D2 = d(0.6, 0, 0, 0.12), D3 = d(0.25, 0, 0, 0.46);
      const edges = [0, 1, 2, 3].map((k) => [D1[k], D3[k]]);
      return [D1, D2, D3, ...edges, d(0.2, -0.98, 0, 0), d(0.2, 0.98, 0, 0), zig(-1.02, 0.07, -0.42), zig(1.02, 0.07, -0.42), [[-1.15, -0.9, -0.42], [1.15, -0.9, -0.42]], [[-1.15, 0.9, -0.42], [1.15, 0.9, -0.42]]];
    }
  };

  function sampleShape(name, N) {
    const lines = (SHAPES[name] || SHAPES.branch)();
    const segs = []; let total = 0;
    lines.forEach((l) => { for (let i = 1; i < l.length; i++) { const a = l[i - 1], b = l[i], len = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]); if (len > 0) { segs.push([a, b, total, len]); total += len; } } });
    const out = new Float32Array(N * 3);
    const lineN = Math.floor(N * 0.9);
    let si = 0;
    for (let k = 0; k < lineN; k++) {
      const pos = ((k + Math.random()) / lineN) * total;
      while (si < segs.length - 1 && segs[si][2] + segs[si][3] < pos) si++;
      const [a, b, start, len] = segs[si], u = (pos - start) / len;
      for (let q = 0; q < 3; q++) out[k * 3 + q] = a[q] + (b[q] - a[q]) * u + (Math.random() - 0.5) * 0.012;
    }
    for (let k = lineN; k < N; k++) {
      const r = 0.35 + Math.random() * 1.15, th = Math.random() * Math.PI * 2, ph = Math.acos(2 * Math.random() - 1);
      out[k * 3] = r * Math.sin(ph) * Math.cos(th) * 1.25; out[k * 3 + 1] = r * Math.cos(ph) * 0.95; out[k * 3 + 2] = r * Math.sin(ph) * Math.sin(th);
    }
    return out;
  }

  /* =========================================================
     2. Particules : rotation 3D en perspective, plan arrière + plan avant
     ========================================================= */
  function Particles(back, front, shapes) {
    const ctx = back.getContext('2d'), ctx2 = front.getContext('2d');
    const N = MOB ? 700 : 2800;
    const seeds = new Float32Array(N * 4);
    for (let k = 0; k < N; k++) {
      const big = Math.random() < 0.04;
      seeds[k * 4] = Math.random();
      seeds[k * 4 + 1] = Math.random() * Math.PI * 2;
      seeds[k * 4 + 2] = big ? 2 + Math.random() * 1.8 : 0.7 + Math.random() * 1;
      seeds[k * 4 + 3] = k >= N * 0.9 ? 0.15 + Math.random() * 0.25 : 0.55 + Math.random() * 0.45;
    }
    const cache = {};
    const sets = shapes.map((s) => cache[s] || (cache[s] = sampleShape(s, N)));
    const sprite = document.createElement('canvas');
    sprite.width = sprite.height = 32;
    const sg = sprite.getContext('2d');
    const grd = sg.createRadialGradient(16, 16, 0, 16, 16, 16);
    grd.addColorStop(0, 'rgba(255,236,190,1)'); grd.addColorStop(0.25, 'rgba(243,210,122,.85)'); grd.addColorStop(0.6, 'rgba(226,150,60,.25)'); grd.addColorStop(1, 'rgba(226,150,60,0)');
    sg.fillStyle = grd; sg.fillRect(0, 0, 32, 32);
    let W = 0, H = 0, cx = 0, cy = 0, R = 0, cur = 0, mix = 0, prog = 0, loc = 0;
    const t0 = performance.now();
    function size(c, x) {
      const dpr = DPR();
      c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
      x.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    function resize() {
      W = back.clientWidth; H = back.clientHeight;
      size(back, ctx); size(front, ctx2);
      const desk = W >= 990;
      cx = desk ? W * 0.58 : W * 0.5; cy = desk ? H * 0.47 : H * 0.3;
      R = Math.min(W, H) * (desk ? 0.6 : 0.5);
    }
    function draw(mx, my) {
      const t = RM ? 0 : (performance.now() - t0) / 1000;
      for (const c of [ctx, ctx2]) { c.globalCompositeOperation = 'source-over'; c.clearRect(0, 0, W, H); c.globalCompositeOperation = 'lighter'; }
      const A = sets[cur], B = sets[Math.min(sets.length - 1, cur + 1)];
      const ay = (1 - mix) * (loc - 0.5) * 0.8 - mix * 0.4 + (RM ? 0 : Math.sin(t * 0.21) * 0.22) + mx * 0.45;
      const ax = -0.12 + my * 0.18 + (RM ? 0 : Math.sin(t * 0.17) * 0.06);
      const az = (prog - 0.5) * 0.25;
      const cA = Math.cos(ay), sA = Math.sin(ay), cX = Math.cos(ax), sX = Math.sin(ax), cZ = Math.cos(az), sZ = Math.sin(az);
      const PZ = 3.4, pxF = mx * 16, pyF = my * 10;
      for (let k = 0; k < N; k++) {
        const d = seeds[k * 4], ph = seeds[k * 4 + 1], sz = seeds[k * 4 + 2];
        let m = (mix - d * 0.4) / 0.6; m = m < 0 ? 0 : m > 1 ? 1 : m; m = m * m * (3 - 2 * m);
        const i3 = k * 3;
        let x = A[i3] + (B[i3] - A[i3]) * m, y = A[i3 + 1] + (B[i3 + 1] - A[i3 + 1]) * m, z = A[i3 + 2] + (B[i3 + 2] - A[i3 + 2]) * m;
        const sw = Math.sin(m * Math.PI) * 0.24;
        x += Math.cos(d * 23 + t * 0.9) * sw; y += Math.sin(d * 19 + t * 0.8) * sw; z += Math.sin(d * 29 + t * 0.7) * sw;
        x += Math.sin(t * 0.7 + ph) * 0.007; y += Math.cos(t * 0.6 + ph * 1.3) * 0.007;
        const x1 = x * cA + z * sA, z1 = -x * sA + z * cA;
        const y1 = y * cX - z1 * sX, z2 = y * sX + z1 * cX;
        const f = PZ / (PZ - z2);
        const xs = (x1 * cZ - y1 * sZ) * f, ys = (x1 * sZ + y1 * cZ) * f;
        const isF = z2 > 0.2;
        const X = cx + xs * R + (isF ? pxF : -pxF * 0.4), Y = cy + ys * R + (isF ? pyF : -pyF * 0.4);
        const dep = clamp(0.85 + (f - 1) * 1.3, 0.4, 1.35);
        const tw = 0.35 + 0.65 * Math.abs(Math.sin(t * 1.4 + ph * 3));
        const c = isF ? ctx2 : ctx;
        c.globalAlpha = Math.min(1, seeds[k * 4 + 3] * tw * dep * (isF ? 0.85 : 1));
        const s = sz * f * (isF ? 1.2 : 1);
        c.drawImage(sprite, X - s * 2, Y - s * 2, s * 4, s * 4);
      }
      ctx.globalAlpha = 1; ctx2.globalAlpha = 1;
    }
    resize();
    return { resize, draw, set(i, m, p, l) { cur = i; mix = m; prog = p; loc = l; } };
  }

  /* =========================================================
     3. Bouteilles en relief : maillage bombé depuis la silhouette (WebGL)
     ========================================================= */
  const M4 = {
    id() { const o = new Float32Array(16); o[0] = o[5] = o[10] = o[15] = 1; return o; },
    mul(a, b) { const o = new Float32Array(16); for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) { let s = 0; for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k]; o[c * 4 + r] = s; } return o; },
    T(x, y, z) { const o = M4.id(); o[12] = x; o[13] = y; o[14] = z; return o; },
    S(x, y, z) { const o = M4.id(); o[0] = x; o[5] = y; o[10] = z; return o; },
    RX(a) { const c = Math.cos(a), s = Math.sin(a), o = M4.id(); o[5] = c; o[6] = s; o[9] = -s; o[10] = c; return o; },
    RY(a) { const c = Math.cos(a), s = Math.sin(a), o = M4.id(); o[0] = c; o[2] = -s; o[8] = s; o[10] = c; return o; },
    RZ(a) { const c = Math.cos(a), s = Math.sin(a), o = M4.id(); o[0] = c; o[1] = s; o[4] = -s; o[5] = c; return o; },
    P(fy, asp, n, f) { const t = 1 / Math.tan(fy / 2), o = new Float32Array(16); o[0] = t / asp; o[5] = t; o[10] = (f + n) / (n - f); o[11] = -1; o[14] = (2 * f * n) / (n - f); return o; }
  };
  const VS = 'attribute vec3 aP;attribute vec3 aN;attribute vec2 aT;uniform mat4 uPV;uniform mat4 uM;uniform mat3 uNM;varying vec2 vT;varying vec3 vN;varying vec3 vW;' +
    'void main(){vec4 w=uM*vec4(aP,1.0);vW=w.xyz;vN=uNM*aN;vT=aT;gl_Position=uPV*w;}';
  const FS = '#ifdef GL_FRAGMENT_PRECISION_HIGH\nprecision highp float;\n#else\nprecision mediump float;\n#endif\n' +
    'uniform sampler2D uTex;uniform float uA;uniform float uDis;uniform float uFloor;uniform float uRefl;uniform float uReflLen;uniform vec3 uL;uniform vec3 uCam;uniform vec2 uSeed;' +
    'varying vec2 vT;varying vec3 vN;varying vec3 vW;' +
    'float hs(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}' +
    'float nz(vec2 p){vec2 i=floor(p),f=fract(p);vec2 u=f*f*(3.0-2.0*f);return mix(mix(hs(i),hs(i+vec2(1.0,0.0)),u.x),mix(hs(i+vec2(0.0,1.0)),hs(i+vec2(1.0,1.0)),u.x),u.y);}' +
    'void main(){vec4 c=texture2D(uTex,vT);if(c.a<0.2)discard;float na=smoothstep(0.25,0.85,c.a);c*=na/c.a;float fd=1.0;' +
    'if(uRefl>0.5){float d=uFloor-vW.y;if(d<0.0)discard;fd=1.0-smoothstep(0.0,uReflLen,d);fd*=fd;}' +
    'float g=0.0;if(uDis>0.001){float n=nz(vT*vec2(11.0,24.0)+uSeed)*0.5+nz(vT*vec2(31.0,66.0)+uSeed*1.7)*0.22+(1.0-vT.y)*0.28;float e=n-(uDis*1.12-0.06);if(e<0.0)discard;g=1.0-smoothstep(0.0,0.05,e);}' +
    'vec3 N=normalize(vN);vec3 V=normalize(uCam-vW);vec3 L=normalize(uL);vec3 H=normalize(L+V);float fv=max(dot(N,V),0.0);c*=0.45+0.55*smoothstep(0.02,0.3,fv);' +
    'float dif=max(dot(N,L),0.0);float sp=pow(max(dot(N,H),0.0),46.0);' +
    'vec3 L2=normalize(vec3(-L.x*0.9,0.25,0.75));float sp2=pow(max(dot(N,normalize(L2+V)),0.0),110.0);' +
    'float rim=pow(1.0-fv,3.0);vec3 gold=vec3(0.886,0.714,0.353);' +
    'vec3 col=c.rgb*(0.8+0.3*dif)+c.a*(vec3(1.0,0.97,0.9)*(sp*0.5+sp2*0.28)+gold*rim*0.6);' +
    'col+=gold*g*1.7*c.a+vec3(1.0,0.93,0.75)*g*g*0.6*c.a;' +
    'gl_FragColor=vec4(col,c.a)*uA*fd;}';

  function GLStage(canvas, imgs, onReady, onFail) {
    let gl = null;
    try { gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: true, powerPreference: 'high-performance' }); } catch (e) { gl = null; }
    if (!gl) return null;
    const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; };
    let prog;
    try {
      prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
    } catch (e) { return null; }
    const loc = {};
    ['aP', 'aN', 'aT'].forEach((n) => (loc[n] = gl.getAttribLocation(prog, n)));
    ['uPV', 'uM', 'uNM', 'uTex', 'uA', 'uDis', 'uFloor', 'uRefl', 'uReflLen', 'uL', 'uCam', 'uSeed'].forEach((n) => (loc[n] = gl.getUniformLocation(prog, n)));
    const objs = imgs.map(() => null);
    let W = 0, H = 0, PV = null, camZ = 1400, dead = false;
    const pot = (v) => { let p = 1; while (p < v) p *= 2; return Math.min(p, 2048); };
    const pick = (im) => {
      let best = im.currentSrc || im.src, bw = 0;
      const cap = MOB ? 800 : 4000;
      (im.getAttribute('srcset') || '').split(',').forEach((s) => { const p = s.trim().split(/\s+/), w = parseInt(p[1], 10); if (p[0] && w > bw && w <= cap) { bw = w; best = p[0]; } });
      return best;
    };
    function build(I) {
      const iw = I.naturalWidth, ih = I.naturalHeight, asp = iw / ih;
      const tc = document.createElement('canvas'); tc.width = pot(iw); tc.height = pot(ih);
      tc.getContext('2d').drawImage(I, 0, 0, tc.width, tc.height);
      const hw = 96, hh = Math.max(48, Math.round(hw / asp));
      const hc = document.createElement('canvas'); hc.width = hw; hc.height = hh;
      const hx = hc.getContext('2d'); hx.drawImage(I, 0, 0, hw, hh);
      const px = hx.getImageData(0, 0, hw, hh).data;
      const C = new Float32Array(hh), Rr = new Float32Array(hh);
      let top = -1, bot = -1;
      for (let y = 0; y < hh; y++) {
        let l = -1, r = -1;
        for (let x = 0; x < hw; x++) if (px[(y * hw + x) * 4 + 3] > 110) { if (l < 0) l = x; r = x; }
        if (l >= 0) { C[y] = ((l + r + 1) / 2 / hw - 0.5) * asp; Rr[y] = ((r - l + 1) / 2 / hw) * asp; if (top < 0) top = y; bot = y; }
      }
      const C2 = new Float32Array(hh), R2 = new Float32Array(hh);
      for (let y = 0; y < hh; y++) {
        if (Rr[y] <= 0) continue;
        let sc = 0, sr = 0, n = 0;
        for (let k = -3; k <= 3; k++) { const yy = y + k; if (yy < 0 || yy >= hh || Rr[yy] <= 0) continue; sc += C[yy]; sr += Rr[yy]; n++; }
        C2[y] = sc / n; R2[y] = sr / n;
      }
      const cols = MOB ? 48 : 72, rows = Math.min(MOB ? 130 : 200, Math.round(cols / asp));
      const nv = (cols + 1) * (rows + 1), Z = new Float32Array(nv);
      const DEP = 0.78, PW = 2.4, edge = Math.max(2, hh * 0.035);
      for (let j = 0; j <= rows; j++) {
        const fy = (j / rows) * (hh - 1), y0 = Math.floor(fy), y1 = Math.min(hh - 1, y0 + 1), w = fy - y0;
        let c, r;
        if (R2[y0] <= 0 && R2[y1] <= 0) { r = 0; c = 0; }
        else if (R2[y0] <= 0) { r = R2[y1] * w; c = C2[y1]; }
        else if (R2[y1] <= 0) { r = R2[y0] * (1 - w); c = C2[y0]; }
        else { r = R2[y0] * (1 - w) + R2[y1] * w; c = C2[y0] * (1 - w) + C2[y1] * w; }
        const tv = top < 0 ? 0 : clamp(Math.min(fy - top, bot - fy) / edge, 0, 1);
        const taper = Math.sin((tv * Math.PI) / 2);
        for (let i = 0; i <= cols; i++) {
          const x = (i / cols - 0.5) * asp, d = r > 0 ? Math.abs(x - c) / r : 2;
          Z[j * (cols + 1) + i] = d < 1 ? DEP * r * Math.pow(1 - Math.pow(d, PW), 1 / PW) * taper : 0;
        }
      }
      const pos = new Float32Array(nv * 3), nor = new Float32Array(nv * 3), uv = new Float32Array(nv * 2);
      const dx = asp / cols, dy = 1 / rows, C1 = cols + 1;
      for (let j = 0; j <= rows; j++) for (let i = 0; i <= cols; i++) {
        const id = j * C1 + i;
        const zl = Z[j * C1 + Math.max(0, i - 1)], zr = Z[j * C1 + Math.min(cols, i + 1)], zu = Z[Math.max(0, j - 1) * C1 + i], zd = Z[Math.min(rows, j + 1) * C1 + i];
        const gx = (zr - zl) / (2 * dx), gy = (zu - zd) / (2 * dy);
        const L = Math.hypot(gx, gy, 1);
        pos[id * 3] = (i / cols - 0.5) * asp; pos[id * 3 + 1] = 0.5 - j / rows; pos[id * 3 + 2] = Z[id];
        nor[id * 3] = -gx / L; nor[id * 3 + 1] = -gy / L; nor[id * 3 + 2] = 1 / L;
        uv[id * 2] = i / cols; uv[id * 2 + 1] = j / rows;
      }
      const idx = new Uint16Array(cols * rows * 6); let q = 0;
      for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) { const a = j * C1 + i, b = a + 1, c = a + C1, d = c + 1; idx[q++] = a; idx[q++] = c; idx[q++] = b; idx[q++] = b; idx[q++] = c; idx[q++] = d; }
      const buf = (data, target) => { const b = gl.createBuffer(); gl.bindBuffer(target, b); gl.bufferData(target, data, gl.STATIC_DRAW); return b; };
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, tc);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return { tex, asp, pb: buf(pos, gl.ARRAY_BUFFER), nb: buf(nor, gl.ARRAY_BUFFER), tb: buf(uv, gl.ARRAY_BUFFER), ib: buf(idx, gl.ELEMENT_ARRAY_BUFFER), n: idx.length };
    }
    const load = (im, i) => new Promise((res, rej) => {
      const I = new Image();
      I.crossOrigin = 'anonymous';
      I.onload = () => { try { objs[i] = build(I); res(); } catch (e) { rej(e); } };
      I.onerror = rej;
      I.src = pick(im);
    });
    function resize() {
      const dpr = DPR();
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
      camZ = clamp(H * 1.55, 900, 1600);
      PV = M4.mul(M4.P(2 * Math.atan(H / 2 / camZ), W / Math.max(1, H), 10, camZ * 4), M4.T(0, 0, -camZ));
    }
    function begin() {
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.useProgram(prog);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.enable(gl.DEPTH_TEST); gl.depthFunc(gl.LEQUAL); gl.disable(gl.CULL_FACE);
      gl.uniformMatrix4fv(loc.uPV, false, PV); gl.uniform3f(loc.uCam, 0, 0, camZ); gl.uniform1i(loc.uTex, 0);
    }
    const n3 = (R) => new Float32Array([R[0], R[1], R[2], R[4], R[5], R[6], R[8], R[9], R[10]]);
    function draw(i, o) {
      const ob = objs[i];
      if (!ob || o.a <= 0.001 || o.dis >= 0.999) return;
      const R = M4.mul(M4.RY(o.ry), M4.mul(M4.RX(o.rx), M4.RZ(o.rz)));
      const M = M4.mul(M4.T(o.x, o.y, -o.s * 0.1), M4.mul(R, M4.S(o.s, o.s, o.s)));
      gl.bindBuffer(gl.ARRAY_BUFFER, ob.pb); gl.enableVertexAttribArray(loc.aP); gl.vertexAttribPointer(loc.aP, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, ob.nb); gl.enableVertexAttribArray(loc.aN); gl.vertexAttribPointer(loc.aN, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, ob.tb); gl.enableVertexAttribArray(loc.aT); gl.vertexAttribPointer(loc.aT, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ob.ib);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, ob.tex);
      gl.uniform3fv(loc.uL, o.L); gl.uniform1f(loc.uDis, o.dis); gl.uniform2f(loc.uSeed, i * 3.1 + 0.7, i * 1.7 + 0.3);
      if (o.refl > 0) {
        const F = M4.S(1, -1, 1);
        gl.clear(gl.DEPTH_BUFFER_BIT);
        gl.uniformMatrix4fv(loc.uM, false, M4.mul(M4.T(0, 2 * o.floor, 0), M4.mul(F, M)));
        gl.uniformMatrix3fv(loc.uNM, false, n3(M4.mul(F, R)));
        gl.uniform1f(loc.uRefl, 1); gl.uniform1f(loc.uFloor, o.floor); gl.uniform1f(loc.uReflLen, o.reflLen); gl.uniform1f(loc.uA, o.a * o.refl);
        gl.drawElements(gl.TRIANGLES, ob.n, gl.UNSIGNED_SHORT, 0);
      }
      gl.clear(gl.DEPTH_BUFFER_BIT);
      gl.uniformMatrix4fv(loc.uM, false, M); gl.uniformMatrix3fv(loc.uNM, false, n3(R));
      gl.uniform1f(loc.uRefl, 0); gl.uniform1f(loc.uA, o.a);
      gl.drawElements(gl.TRIANGLES, ob.n, gl.UNSIGNED_SHORT, 0);
    }
    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); if (!dead) { dead = true; onFail(); } });
    resize();
    Promise.all(imgs.map(load)).then(() => { if (!dead) onReady(); }).catch(() => { if (!dead) { dead = true; onFail(); } });
    return { resize, begin, draw, asp: (i) => (objs[i] ? objs[i].asp : 0.4), ok: () => !dead };
  }

  /* =========================================================
     4. Scène pilotée au scroll
     ========================================================= */
  function stage(root) {
    const chs = Array.from(root.querySelectorAll('.dd-stage__ch'));
    const N = chs.length;
    if (!N) return;
    const imgs = Array.from(root.querySelectorAll('.dd-stage__p'));
    const imgFor = chs.map((c, i) => { for (let j = i; j >= 0; j--) { const im = imgs.find((x) => +x.dataset.i === j); if (im) return im; } return imgs[0]; });
    const K = chs.map((c) => ({ rot: +c.dataset.rot || 0, tilt: +c.dataset.tilt || 0, s: (+c.dataset.scale || 100) / 100, x: +c.dataset.x || 0, y: +c.dataset.y || 0, shape: c.dataset.shape || 'branch' }));
    const nav = Array.from(root.querySelectorAll('.dd-stage__nav button'));
    const cur = root.querySelector('[data-cur]');
    const bar = root.querySelector('.dd-stage__bar');
    const glow = root.querySelector('.dd-stage__glow');
    const prods = root.querySelector('.dd-stage__prods');
    const sticky = root.querySelector('.dd-stage__sticky');
    const canvas = root.querySelector('.dd-stage__fx');
    const mk = (cls) => { const c = document.createElement('canvas'); c.className = cls; c.setAttribute('aria-hidden', 'true'); c.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;display:block'; return c; };
    const glc = mk('dd-stage__gl'), fx2 = mk('dd-stage__fx2');
    if (prods) { prods.after(glc); glc.after(fx2); }
    const fx = canvas && !LITE ? Particles(canvas, fx2, K.map((k) => k.shape)) : null;
    if (LITE && canvas) canvas.style.display = 'none';
    let fc = 0, active = 0, intro = RM ? 1 : 0, mx = 0, my = 0, mxs = 0, mys = 0, useGL = false, waitGL = false;
    const t0 = performance.now();
    let introT0 = t0;
    const G = imgs.length && prods && !LITE ? GLStage(glc, imgs,
      () => { useGL = true; imgs.forEach((im) => (im.style.visibility = 'hidden')); root.classList.add('has-gl'); waitGL = false; if (!RM) { introT0 = performance.now(); intro = 0; } },
      () => { useGL = false; imgs.forEach((im) => (im.style.visibility = '')); glc.style.display = 'none'; root.classList.remove('has-gl'); waitGL = false; }) : null;
    if (!G) glc.style.display = 'none'; else waitGL = true;
    if (FINE && !RM) addEventListener('pointermove', (e) => { mx = (e.clientX / innerWidth) * 2 - 1; my = (e.clientY / innerHeight) * 2 - 1; }, { passive: true });
    function setActive(k) {
      if (k === active) return;
      active = k;
      chs.forEach((c, j) => c.classList.toggle('is-on', j === k));
      nav.forEach((b, j) => b.classList.toggle('is-on', j === k));
      if (cur) cur.textContent = String(k + 1).padStart(2, '0');
    }
    function frame() {
      const now = performance.now(), t = (now - t0) / 1000;
      const r = root.getBoundingClientRect();
      const total = Math.max(1, N > 1 ? root.offsetHeight - innerHeight : root.offsetHeight);
      const p = clamp(-r.top / total, 0, 1);
      const f = p * N;
      const i = Math.min(N - 1, Math.floor(f));
      const tt = f - i;
      const w = 0.34;
      const m = i < N - 1 && tt > 1 - w ? ease((tt - (1 - w)) / w) : 0;
      const a = K[i], b = K[Math.min(N - 1, i + 1)];
      const L = (u, v) => u + (v - u) * m;
      if (!RM && intro < 1) intro = Math.min(1, (now - introT0) / (useGL ? 2300 : 1600));
      const ie = 1 - Math.pow(1 - intro, 3);
      mxs += (mx - mxs) * 0.06; mys += (my - mys) * 0.06;
      const drift = (tt - 0.5) * (1 - m);
      const sm = innerWidth < 990 ? 0.35 : 1;
      const rot = L(a.rot, b.rot) + drift * 4, tilt = L(a.tilt, b.tilt) + drift * 10, s = (1 + (L(a.s, b.s) - 1) * sm) * (0.86 + 0.14 * ie), x = L(a.x, b.x) * sm, y = L(a.y, b.y) * sm + (1 - ie) * 6;
      const imA = imgFor[i], imB = imgFor[Math.min(N - 1, i + 1)];
      const W = (sticky && sticky.clientWidth) || innerWidth, H = (sticky && sticky.clientHeight) || innerHeight;
      if (glow) glow.style.transform = 'translate(-50%,-50%) translate(' + ((x * W) / 100).toFixed(1) + 'px,' + ((y * H) / 100).toFixed(1) + 'px)';
      if (useGL && G && G.ok() && imA) {
        const hpx = imA.offsetHeight || H * 0.7;
        const bx = imA.offsetLeft - W / 2 + (x * W) / 100, by = H / 2 - (imA.offsetTop + (y * H) / 100);
        const bob = RM ? 0 : Math.sin(t * 1.05) * hpx * 0.012;
        const sway = RM ? 0 : Math.sin(t * 0.55) * 3.5;
        const ry = (tilt + sway + mxs * 11) * D2R, rx = (mys * 6 + (RM ? 0 : Math.sin(t * 0.8) * 1.2)) * D2R, rz = -rot * D2R;
        const lx = Math.sin(p * Math.PI * 2.4 + t * 0.14) * 0.95 + mxs * 0.6;
        const Lv = new Float32Array([lx, 0.42 - mys * 0.3, 0.85]);
        const refl = W < 990 ? 0 : 0.24;
        const floorOf = (idx, sc) => by - ((hpx * sc) / 2) * Math.cos(rz) - ((hpx * sc * G.asp(idx)) / 2) * Math.abs(Math.sin(rz)) - 8;
        G.begin();
        const ia = imgs.indexOf(imA), ib = imgs.indexOf(imB);
        if (imA === imB || ib < 0) {
          G.draw(ia, { x: bx, y: by + bob, ry, rx, rz, s: hpx * s, dis: RM ? 0 : 1 - ie, a: 1, refl, floor: floorOf(ia, s), reflLen: hpx * s * 0.34, L: Lv });
        } else {
          const sA = s * (1 - m * 0.12), sB = s * (0.88 + 0.12 * m);
          G.draw(ia, { x: bx, y: by + bob + m * H * 0.03, ry: ry - m * 0.45, rx, rz, s: hpx * sA, dis: Math.max(RM ? 0 : 1 - ie, clamp(m / 0.72, 0, 1)), a: 1, refl, floor: floorOf(ia, sA), reflLen: hpx * sA * 0.34, L: Lv });
          G.draw(ib, { x: bx, y: by + bob - (1 - m) * H * 0.04, ry: ry + (1 - m) * 0.6, rx, rz, s: hpx * sB, dis: clamp((1 - m) / 0.72, 0, 1), a: 1, refl, floor: floorOf(ib, sB), reflLen: hpx * sB * 0.34, L: Lv });
        }
      } else if (waitGL && now - t0 < 1500) {
        imgs.forEach((im) => (im.style.opacity = 0));
        introT0 = now;
      } else {
        const base = 'translate(-50%,-50%) translate3d(' + x.toFixed(2) + 'vw,' + y.toFixed(2) + 'vh,0) rotateY(' + tilt.toFixed(2) + 'deg) rotate(' + rot.toFixed(2) + 'deg) scale(' + s.toFixed(3) + ')';
        imgs.forEach((im) => { if (im !== imA && im !== imB) im.style.opacity = 0; });
        if (imA) {
          if (imA === imB || !imB) { imA.style.opacity = ie; imA.style.transform = base; }
          else {
            imA.style.opacity = (1 - m) * ie; imA.style.transform = base + ' scale(' + (1 - m * 0.3).toFixed(3) + ') translateY(' + (-m * 6).toFixed(2) + 'vh)';
            imB.style.opacity = m; imB.style.transform = base + ' scale(' + (0.7 + m * 0.3).toFixed(3) + ') translateY(' + ((1 - m) * 8).toFixed(2) + 'vh)';
          }
        }
      }
      setActive(m > 0.5 ? Math.min(N - 1, i + 1) : i);
      if (bar) bar.style.setProperty('--sp', p.toFixed(4));
      fc++;
      if (fx && (!MOB || (fc & 1))) { fx.set(i, m, p, tt); fx.draw(mxs, mys); }
    }
    nav.forEach((btn, j) => btn.addEventListener('click', () => {
      const top = root.getBoundingClientRect().top + scrollY;
      const total = root.offsetHeight - innerHeight;
      window.scrollTo({ top: top + total * ((j + 0.3) / N), behavior: RM ? 'auto' : 'smooth' });
    }));
    const down = root.querySelector('.dd-stage__down');
    if (down) down.addEventListener('click', (e) => {
      const id = (down.getAttribute('href') || '').slice(1);
      const tg = id && document.getElementById(id);
      if (!tg) return;
      e.preventDefault();
      tg.scrollIntoView({ behavior: RM ? 'auto' : 'smooth' });
    });
    let raf = 0, vis = true;
    const loop = () => { frame(); raf = vis ? requestAnimationFrame(loop) : 0; };
    if ('IntersectionObserver' in window) new IntersectionObserver((es) => { vis = es[0].isIntersecting; if (vis && !raf) raf = requestAnimationFrame(loop); }, { threshold: 0 }).observe(root);
    addEventListener('resize', () => { if (fx) fx.resize(); if (G) G.resize(); }, { passive: true });
    chs.forEach((c, j) => c.classList.toggle('is-on', j === 0));
    raf = requestAnimationFrame(loop);
  }

  const run = () => document.querySelectorAll('[data-dd-stage]').forEach(stage);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();
